/**
 * Invoice Service - Generates and manages invoices for payments
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { formatAmount } from './cashfreeService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generate invoice number
 * @returns {string} Invoice number
 */
const generateInvoiceNumber = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const timestamp = Date.now().toString().slice(-6);
  return `INV-${year}${month}-${timestamp}`;
};

/**
 * Create invoice for a payment
 * @param {Object} invoiceData - Invoice details
 * @returns {Promise<Object>} Result with invoice ID
 */
export const createInvoice = async (invoiceData) => {
  try {
    const invoiceNumber = generateInvoiceNumber();

    const invoice = {
      invoiceNumber,
      userId: invoiceData.userId,
      paymentId: invoiceData.paymentId,

      // Support both Razorpay and Cashfree (backward compatible)
      razorpayPaymentId: invoiceData.razorpayPaymentId || null,
      razorpayOrderId: invoiceData.razorpayOrderId || null,
      cashfreePaymentId: invoiceData.cashfreePaymentId || null,
      cashfreeOrderId: invoiceData.cashfreeOrderId || null,

      // Customer details
      customerName: invoiceData.customerName,
      customerEmail: invoiceData.customerEmail,
      customerPhone: invoiceData.customerPhone,

      // Package details
      packageId: invoiceData.packageId,
      packageName: invoiceData.packageName,
      packageDuration: invoiceData.packageDuration,

      // Amount breakdown
      baseAmount: invoiceData.baseAmount,
      gstRate: invoiceData.gstRate || 18,
      gstAmount: invoiceData.gstAmount,
      totalAmount: invoiceData.totalAmount,
      currency: invoiceData.currency || 'INR',

      // Dates
      invoiceDate: Timestamp.now(),
      paymentDate: invoiceData.paymentDate || Timestamp.now(),

      // Status
      status: 'paid',

      // Additional metadata
      metadata: invoiceData.metadata || {},

      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'invoices'), invoice);

    console.log('✅ Invoice created:', invoiceNumber);

    return {
      success: true,
      invoiceId: docRef.id,
      invoiceNumber,
      invoice
    };
  } catch (error) {
    console.error('❌ Error creating invoice:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user invoices
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User invoices
 */
export const getUserInvoices = async (userId) => {
  try {
    const q = query(
      collection(db, 'invoices'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const invoices = [];

    querySnapshot.forEach((doc) => {
      invoices.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return { success: true, invoices };
  } catch (error) {
    console.error('❌ Error fetching user invoices:', error);
    return { success: false, error: error.message, invoices: [] };
  }
};

/**
 * Generate invoice HTML for print (Kept for Print functionality)
 * @param {Object} invoice - Invoice data
 * @returns {string} HTML content
 */
export const generateInvoiceHTML = (invoice) => {
  const invoiceDate = invoice.invoiceDate?.toDate?.() || new Date();
  const paymentDate = invoice.paymentDate?.toDate?.() || new Date();

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice - ${invoice.invoiceNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: sans-serif; padding: 40px; background: white; }
    .header { display: flex; justify-content: space-between; border-bottom: 2px solid #4F46E5; padding-bottom: 20px; margin-bottom: 30px; }
    .company h1 { color: #4F46E5; }
    .invoice-details { text-align: right; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th { background: #4F46E5; color: white; padding: 10px; text-align: left; }
    td { padding: 10px; border-bottom: 1px solid #ddd; }
    .totals { text-align: right; margin-top: 20px; }
    .footer { margin-top: 50px; text-align: center; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="company">
      <h1>Udaan Jobs</h1>
      <p>Job Portal & Recruitment Services</p>
    </div>
    <div class="invoice-details">
      <h2>INVOICE</h2>
      <p>#${invoice.invoiceNumber}</p>
      <p>Date: ${invoiceDate.toLocaleDateString('en-IN')}</p>
    </div>
  </div>
  <div class="customer">
    <h3>Bill To:</h3>
    <p>${invoice.customerName}</p>
    <p>${invoice.customerEmail}</p>
  </div>
  <table>
    <tr><th>Description</th><th>Price</th><th>Total</th></tr>
    <tr>
      <td>${invoice.packageName} (${invoice.packageDuration} days)</td>
      <td>${formatAmount(invoice.baseAmount)}</td>
      <td>${formatAmount(invoice.baseAmount)}</td>
    </tr>
  </table>
  <div class="totals">
    <p>Subtotal: ${formatAmount(invoice.baseAmount)}</p>
    <p>GST (${invoice.gstRate}%): ${formatAmount(invoice.gstAmount)}</p>
    <h3>Total: ${formatAmount(invoice.totalAmount)}</h3>
  </div>
  <div class="footer">
    <p>Thank you for your business!</p>
  </div>
</body>
</html>
  `;
};

/**
 * Download invoice as PDF (Professional Format using jsPDF)
 * @param {Object} invoice - Invoice data
 */
/**
 * Generate PDF document object (Internal Helper)
 * @param {Object} invoice - Invoice data
 * @returns {jsPDF} jsPDF document object
 */
const createInvoicePdfDoc = (invoice) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;

  // Helper for currency formatting
  const formatCurrency = (amount) => {
    // usage of 'Rs.' prefix instead of symbol to avoid font encoding issues in PDF
    return 'Rs. ' + new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const invoiceDate = invoice.invoiceDate?.toDate?.() || new Date();

  // --- MODERN LAYOUT ---

  // 1. Top Header Background
  doc.setFillColor(79, 70, 229); // Indigo
  doc.rect(0, 0, pageWidth, 40, 'F');

  // 2. Company Name (White on Indigo)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(255, 255, 255);
  doc.text('Udaan Jobs', 14, 25);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Job Portal & Recruitment Services', 14, 32);

  // 3. Invoice Label (Top Right)
  doc.setFontSize(30);
  doc.setTextColor(255, 255, 255);
  doc.setCharSpace(2); // Spacing for modern look
  doc.text('INVOICE', pageWidth - 14, 28, { align: 'right' });
  doc.setCharSpace(0); // Reset

  // 4. Info Section (Details Grid)
  const infoTop = 55;

  // Left Column - Bill To
  doc.setTextColor(100);
  doc.setFontSize(10);
  doc.text('BILLED TO', 14, infoTop);

  doc.setTextColor(0);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.customerName || 'Valued Customer', 14, infoTop + 7);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  doc.text(invoice.customerEmail || '', 14, infoTop + 13);
  if (invoice.customerPhone) {
    doc.text(invoice.customerPhone, 14, infoTop + 18);
  }

  // Right Column - Invoice Specifics
  const rightColX = pageWidth - 70;

  // Invoice #
  doc.setTextColor(100);
  doc.setFontSize(10);
  doc.text('INVOICE NUMBER', rightColX, infoTop);
  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.invoiceNumber, rightColX, infoTop + 5);

  // Date
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.text('DATE OF ISSUE', rightColX, infoTop + 15);
  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.text(invoiceDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }), rightColX, infoTop + 20);

  // 5. "PAID" Stamp/Badge
  doc.setDrawColor(16, 185, 129); // Green border
  doc.setTextColor(16, 185, 129); // Green text
  doc.setLineWidth(1);
  doc.roundedRect(pageWidth - 50, 80, 36, 12, 2, 2, 'D');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('PAID', pageWidth - 32, 87, { align: 'center' });

  // 6. Modern Table
  autoTable(doc, {
    startY: 105,
    head: [['ITEM PROPSAL / DESCRIPTION', 'QUANTITY', 'PRICE', 'AMOUNT']],
    body: [
      [
        { content: invoice.packageName + '\n' + (invoice.packageDuration + ' days validity'), styles: { minCellHeight: 15 } },
        '1',
        formatCurrency(invoice.baseAmount),
        formatCurrency(invoice.baseAmount)
      ]
    ],
    theme: 'plain', // Clean modern look (custom borders)
    styles: {
      fontSize: 10,
      cellPadding: 8,
      textColor: 50
    },
    headStyles: {
      fillColor: [243, 244, 246], // Light gray background
      textColor: [100], // Gray text
      fontStyle: 'bold',
      lineColor: [229, 231, 235],
      lineWidth: { bottom: 1, top: 1 }
    },
    bodyStyles: {
      lineColor: [229, 231, 235],
      lineWidth: { bottom: 1 }
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      2: { halign: 'right' },
      3: { halign: 'right', fontStyle: 'bold' }
    }
  });

  // 7. Totals Section
  const finalY = doc.lastAutoTable.finalY + 15;
  const summaryX = pageWidth - 80;

  // Background for total
  doc.setFillColor(249, 250, 251);
  doc.rect(summaryX - 5, finalY - 5, 80, 40, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100);

  // Subtotal
  doc.text('Subtotal', summaryX, finalY);
  doc.setTextColor(0);
  doc.text(formatCurrency(invoice.baseAmount), pageWidth - 14, finalY, { align: 'right' });

  // GST
  doc.setTextColor(100);
  doc.text(`GST (${invoice.gstRate || 18}%)`, summaryX, finalY + 10);
  doc.setTextColor(0);
  doc.text(formatCurrency(invoice.gstAmount), pageWidth - 14, finalY + 10, { align: 'right' });

  // Grand Total
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(79, 70, 229); // Indigo
  doc.text('Total Amount', summaryX, finalY + 25);
  doc.text(formatCurrency(invoice.totalAmount), pageWidth - 14, finalY + 25, { align: 'right' });

  // --- FOOTER ---
  const footerY = 270;
  doc.setFontSize(9);
  doc.setTextColor(150);
  doc.text('Thank you for your business!', pageWidth / 2, footerY, { align: 'center' });
  doc.text('For queries, contact support@udaanjobs.com', pageWidth / 2, footerY + 5, { align: 'center' });
  doc.text('This is a computer-generated invoice.', pageWidth / 2, footerY + 10, { align: 'center' });

  return doc;
};

/**
 * Download invoice as PDF (Professional Format using jsPDF)
 * @param {Object} invoice - Invoice data
 */
export const downloadInvoice = (invoice) => {
  const doc = createInvoicePdfDoc(invoice);
  doc.save(`Invoice-${invoice.invoiceNumber}.pdf`);
};

/**
 * View invoice as PDF in new tab
 * @param {Object} invoice - Invoice data
 */
export const viewInvoice = (invoice) => {
  const doc = createInvoicePdfDoc(invoice);
  window.open(doc.output('bloburl'), '_blank');
};

/**
 * Print invoice
 * @param {Object} invoice - Invoice data
 */
export const printInvoice = (invoice) => {
  const html = generateInvoiceHTML(invoice);
  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 250);
};

export default {
  createInvoice,
  getUserInvoices,
  generateInvoiceHTML,
  downloadInvoice,
  printInvoice,
};
